import React, { useState, useMemo, useCallback, useEffect } from 'react'
import qs from 'query-string'
import { useSelector, useDispatch } from 'react-redux'
import { STATE_KEYS } from 'state/requests'
import { selectRequestsAreLoading } from 'state'
import { useLocation } from 'react-router-dom'
import { selectEditingCampaign } from 'state/campaigns'
import { ClickAwayListener } from '@mui/material'
import { selectCommentsByIdentifier, addComment, updateCurrentField, selectCurrentField } from 'state/comments'
import { useTranslation } from 'react-i18next'
import { useWindowHeight, useWindowSize } from 'hooks'
import { useSetIdentifierRoute } from 'hooks/comment'
import { selectProfile } from 'state'
import { PopperContainer, MobileCommentContainer, ThreadListContainer } from './styles'
import FieldCommentsCard from './FieldCommentCards'
import AddComment from 'particles/Comment/AddComment'
import Popper from 'particles/Popper'
import Text from 'particles/Text'
import IconButton from 'particles/Button/IconButton'
import { usePermission } from 'hooks/permissions'
import { CampaignStatusControl } from 'components/Shared/Permission/enums'

import { isRunningInStorybook } from 'particles/utils'
import RectangleButton from 'particles/Button/RectangleButton'
import { scrollSelectedCommentIntoView } from 'components/utils/comments'

interface CommentContainerProp {
    identifier?: string
}

const CommentContainer: React.FC<CommentContainerProp> = ({ children, identifier }) => {
    const [t] = useTranslation()
    const [anchorEl, setAnchorEl] = useState<{ anchor: HTMLElement | null; isThread?: boolean }>({
        anchor: null,
        isThread: false,
    })
    const [newComment, setNewComment] = useState<string>('')
    const [isLoading, setIsLoading] = useState(false)
    const [isCommentCenterElementInView, setIsCommentCenterElementInView] = useState(false)
    const [updateRoute] = useSetIdentifierRoute()
    const dispatch = useDispatch()
    const router = useLocation()
    const profileDetail = useSelector(selectProfile)
    const campaign = useSelector(selectEditingCampaign)
    const currentField = useSelector(selectCurrentField)
    const height = useWindowHeight()
    const windowWidth = useWindowSize()
    const user = { uuid: profileDetail?.uuid, name: profileDetail?.name }
    const switchPopperPositionWidth =
        router && ['tasks', 'template'].some(str => router?.pathname.includes(str)) ? 1520 : 1180
    const params = qs.parse(location.search)
    const { drawer, selectedIdentifier, selectedThread } = params
    const isCommentCenterOpened = drawer === 'comments'
    const fieldComments = useSelector(selectCommentsByIdentifier(campaign?.uuid, identifier))
    const isCommentLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.GetCampaignComments]))

    const canReviewCampaign = usePermission(CampaignStatusControl.ReviewCampaign)

    const visibleFieldComments = Array.isArray(fieldComments)
        ? fieldComments?.filter(({ resolved_at }) => resolved_at === null)
        : []
    const firstThreadId = visibleFieldComments[0]?.uuid

    const childSetAnchorElement = useCallback(
        (e: React.MouseEvent<HTMLElement>, targetElement: HTMLDivElement, isThread?: boolean) => {
            const hasComments = visibleFieldComments.length > 0
            e.stopPropagation()
            if (identifier) {
                const updateRouteIdentifier = selectedIdentifier === identifier ? null : identifier
                if (hasComments) {
                    updateRoute({
                        identifier: updateRouteIdentifier,
                        threadId: firstThreadId,
                        stepUuid: null,
                        section: null,
                    })
                }
            }
            if (isCommentCenterOpened && visibleFieldComments.length) {
                return
            } else {
                setAnchorEl({
                    anchor: anchorEl.anchor ? null : targetElement ? targetElement : e.currentTarget,
                    isThread: isThread,
                })
                dispatch(
                    updateCurrentField({
                        identifier: (anchorEl && currentField === identifier) || !identifier ? null : identifier,
                    }),
                )
            }
        },
        [
            identifier,
            anchorEl,
            currentField,
            visibleFieldComments.length,
            isCommentCenterOpened,
            selectedIdentifier,
            firstThreadId,
        ],
    )

    const isCommentable: boolean = useMemo(() => {
        if (!campaign) {
            return false
        }
        if (!profileDetail?.is_internal_staff) {
            return true
        }
        if (canReviewCampaign && profileDetail?.is_internal_staff) {
            return true
        } else {
            return visibleFieldComments.length > 0
        }
    }, [campaign, fieldComments, profileDetail])

    const cardMaxHeight = useMemo(() => {
        //80vh - (container vertical padding + title size + gap)
        return height ? (windowWidth <= 768 ? height * 0.8 - 110 : 0.4 * height) : undefined
    }, [height, windowWidth])

    const onCommentChange = (comment?: string) => {
        setNewComment(comment || '')
    }

    const onAddComment = useCallback(() => {
        if (campaign && campaign.uuid && !isLoading && identifier) {
            const commentField = document.getElementById(identifier)
            if (Boolean(newComment)) {
                setIsLoading(true)
                dispatch(
                    addComment({
                        uuid: campaign.uuid,
                        field_identifier: identifier,
                        comment: newComment,
                        onSuccessCallback: () => {
                            setNewComment('')
                            setIsLoading(false)
                            setAnchorEl({ anchor: commentField, isThread: false })
                            if (isCommentCenterOpened) {
                                updateRoute({ identifier: identifier, taskUuid: null, stepUuid: null, section: null })
                                setAnchorEl({ ...anchorEl, anchor: null })
                            }
                        },
                        onErrorCallback: () => {
                            setIsLoading(false)
                        },
                    }),
                )
            }
        }
    }, [identifier, campaign, isLoading, newComment])

    const getCommentItem = useMemo(() => {
        const cardWidth = windowWidth <= 768 ? windowWidth - 32 : 296
        return (
            <>
                {visibleFieldComments[0]?.comments && !anchorEl.isThread ? (
                    <ThreadListContainer>
                        <div className="thread-list-header">
                            <Text scale="bodyS700">{t('campaignEditor.comment.commentCenterTitle')}</Text>
                            {canReviewCampaign || !profileDetail?.is_internal_staff ? (
                                <RectangleButton
                                    label={t('button.addNewThread')}
                                    size="s"
                                    icon="plus"
                                    form="text"
                                    color="neutral"
                                    iconForm="regular"
                                    onClick={e => {
                                        e.preventDefault()
                                        setAnchorEl({ ...anchorEl, isThread: true })
                                    }}
                                />
                            ) : null}
                        </div>

                        {visibleFieldComments.map(thread => {
                            return (
                                <FieldCommentsCard
                                    key={thread.uuid}
                                    fieldComments={thread.comments}
                                    identifier={identifier}
                                    cardClassName="popper-comment-card"
                                    cardWidth={cardWidth}
                                    cardMaxHeight={cardMaxHeight}
                                    onDeleteCommentThread={() => setAnchorEl({ ...anchorEl, anchor: null })}
                                    isResolved={Boolean(thread.resolved_at)}
                                    threadId={thread.uuid}
                                    onResolveThread={() => {
                                        // Close comment card when the last thread is resolved
                                        if (visibleFieldComments.length === 1) {
                                            setAnchorEl({ ...anchorEl, anchor: null })
                                        }
                                    }}
                                />
                            )
                        })}
                    </ThreadListContainer>
                ) : (
                    <AddComment
                        profile={user}
                        onPrimaryButtonClick={onAddComment}
                        isLoading={isLoading}
                        onCommentChange={onCommentChange}
                        autoClearComment={false}
                        maxTextAreaHeight={cardMaxHeight}
                        value={newComment}
                        primaryButtonLabel={t('button.comment')}
                        width={cardWidth}
                        placeholder={t('placeholderText.addComment')}
                    />
                )}
            </>
        )
    }, [
        visibleFieldComments,
        user,
        onAddComment,
        isLoading,
        cardMaxHeight,
        newComment,
        windowWidth,
        isCommentCenterOpened,
    ])

    const isCommented = useMemo(() => {
        return (
            Array.isArray(fieldComments) &&
            fieldComments.length > 0 &&
            fieldComments?.some(thread => !Boolean(thread.resolved_at))
        )
    }, [fieldComments])

    useEffect(() => {
        if (currentField !== identifier) {
            setAnchorEl({ ...anchorEl, anchor: null })
        }
    }, [currentField])

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsCommentCenterElementInView(entry.isIntersecting)
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 1,
            },
        )

        if (selectedIdentifier) {
            const commentCenterElement = document.querySelector(`[data-id="${selectedIdentifier}-${selectedThread}"]`)
            if (commentCenterElement) {
                observer.observe(commentCenterElement)
                // scroll to comment center threads
                scrollSelectedCommentIntoView(commentCenterElement, 'center')
            }
        }

        return () => {
            if (selectedIdentifier) {
                const commentCenterElement = document.querySelector(
                    `[data-id="${selectedIdentifier}-${selectedThread}"]`,
                )
                if (commentCenterElement) {
                    observer.unobserve(commentCenterElement)
                }
            }
        }
    }, [selectedIdentifier, isCommentLoading, selectedThread])

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting && isCommentCenterElementInView) {
                    if (selectedIdentifier && typeof selectedIdentifier === 'string') {
                        const editorField = document.getElementById(selectedIdentifier)

                        if (editorField) {
                            editorField.scrollIntoView({ block: 'center', behavior: 'smooth' })
                        }
                    }
                }
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 1,
            },
        )

        if (isCommentCenterElementInView && selectedIdentifier && typeof selectedIdentifier === 'string') {
            const editorField = document.getElementById(selectedIdentifier)
            if (editorField) {
                observer.observe(editorField)
            }
        }

        return () => {
            observer.disconnect()
        }
    }, [selectedIdentifier, isCommentCenterElementInView, isCommentLoading])

    return (
        <>
            {React.Children.map(children, (child: any) => {
                return React.cloneElement(
                    child,
                    isRunningInStorybook()
                        ? {}
                        : {
                              id: identifier ? identifier : undefined,
                              className: 'field-title',
                              onCommentClick: childSetAnchorElement,
                              isCommentable: Boolean(isCommentable && identifier),
                              isCommented: isCommented,
                              icon: Boolean(fieldComments) ? 'chatText' : 'commentPlus',
                              isActivated:
                                  Boolean(anchorEl.anchor) || (selectedIdentifier && selectedIdentifier === identifier),
                          },
                )
            })}
            {identifier && currentField === identifier ? (
                windowWidth <= 768 && Boolean(anchorEl) ? (
                    <MobileCommentContainer>
                        <ClickAwayListener
                            onClickAway={() => {
                                setAnchorEl({ ...anchorEl, anchor: null })
                                dispatch(updateCurrentField({ identifier: null }))
                                updateRoute(null)
                            }}
                        >
                            <PopperContainer className="mobile-container" id="container-background">
                                <IconButton
                                    form="icon"
                                    size="l"
                                    color="neutral"
                                    icon="x"
                                    iconForm="regular"
                                    className="close-icon"
                                    type="button"
                                    onClick={() => {
                                        setAnchorEl({ anchor: null, isThread: false })
                                        dispatch(updateCurrentField({ identifier: null }))
                                        updateRoute(null)
                                    }}
                                />
                                <Text scale="H3">{t('submission.review.commentHistoryHeader')}</Text>
                                <div>{getCommentItem}</div>
                            </PopperContainer>
                        </ClickAwayListener>
                    </MobileCommentContainer>
                ) : (
                    <Popper
                        anchorEl={Boolean(anchorEl.anchor && isCommentable) ? anchorEl.anchor : null}
                        onClose={() => {
                            setAnchorEl({ ...anchorEl, anchor: null })
                            dispatch(updateCurrentField({ identifier: null }))
                            updateRoute(null)
                        }}
                        placement={windowWidth < switchPopperPositionWidth ? 'bottom-end' : 'right-start'}
                        style={{
                            zIndex: 300,
                            maxHeight: '50vh',
                            overflowY: 'auto',
                        }}
                        disablePortal={!isCommentCenterOpened}
                    >
                        <PopperContainer>
                            {isCommentCenterOpened && visibleFieldComments.length ? null : getCommentItem}
                        </PopperContainer>
                    </Popper>
                )
            ) : null}
        </>
    )
}
export default CommentContainer
export { fieldIdentifiers } from './constant'
